export const ORGANIZATION_SETTINGS_SLOT = "organizations.settings.v1";
export const ORGANIZATION_SETTINGS_SUBMIT = "organizations.settings.submit";
const ORGANIZATION_SETTINGS_RETRIEVE = /^organizations\.settings\.retrieve\.([a-z0-9][a-z0-9._-]{0,63})$/;
const INSTALLATION_ID = /^[A-Za-z0-9-]{1,64}$/;

const CONTRIBUTION_ID = /^[a-z0-9][a-z0-9-]{0,63}$/;
const V2_ID = /^[A-Za-z0-9][A-Za-z0-9._-]{1,79}$/;
const V2_EXTENSION_VIEW_SLOT = "extension.view";

export interface OrganizationSettingsTarget {
  publisher: string;
  key: string;
}

export interface OrganizationSettingsInstallation {
  id?: unknown;
  publisher?: unknown;
  key?: unknown;
  api_version?: unknown;
  state: string;
  dependencies?: readonly unknown[];
  ui?: readonly {
    slot?: unknown;
    id?: unknown;
    view?: unknown;
  }[];
  v2?: {
    views?: readonly unknown[];
    ui?: readonly unknown[];
  };
}

interface OrganizationSettingsTabBase {
  id: string;
  label: string;
  href: string;
}

/** The v1 card bridge; it forwards form values to a dependency extension. */
export interface OrganizationSettingsLegacyTab extends OrganizationSettingsTabBase {
  format: "v1";
  retrieving: string;
  unavailable: string;
  retrieveOperation: string | null;
  extensionInstallationId: string | null;
  settingsTargets: OrganizationSettingsTarget[];
  view: {
    type: "card";
    title: string;
    description?: string;
    children: unknown[];
  };
}

/** A declarative v2 view rendered by the host. */
export interface OrganizationSettingsV2Tab extends OrganizationSettingsTabBase {
  format: "v2";
  extensionInstallationId: string;
  viewId: string;
}

export type OrganizationSettingsTab =
  | OrganizationSettingsLegacyTab
  | OrganizationSettingsV2Tab;

function extensionPart(value: unknown): string | null {
  return typeof value === "string" && /^[a-z0-9-]{2,40}$/.test(value) ? value : null;
}

function installationIdentity(value: unknown): OrganizationSettingsTarget | null {
  if (!value || typeof value !== "object") return null;
  const identity = value as { publisher?: unknown; key?: unknown };
  const publisher = extensionPart(identity.publisher);
  const key = extensionPart(identity.key);
  return publisher && key ? { publisher, key } : null;
}

function dependencyIdentity(value: unknown): OrganizationSettingsTarget | null {
  if (!value || typeof value !== "object") return null;
  const dependency = value as { publisher?: unknown; key?: unknown };
  return installationIdentity(dependency)
    ?? installationIdentity(dependency.key);
}

function identityKey(identity: OrganizationSettingsTarget): string {
  return `${identity.publisher}/${identity.key}`;
}

/**
 * A settings card forwards its values to one enabled installed dependency.
 * The dependency extension owns the storage and validation; the host stores
 * no value and creates no platform secret or grant.
 */
function installedDependencies(
  installation: OrganizationSettingsInstallation,
  enabled: ReadonlySet<string>,
): OrganizationSettingsTarget[] {
  const dependencies = new Map<string, OrganizationSettingsTarget>();
  const declared = Array.isArray(installation.dependencies) ? installation.dependencies : [];
  for (const dependency of declared) {
    const identity = dependencyIdentity(dependency);
    if (!identity || !enabled.has(identityKey(identity))) continue;
    dependencies.set(identityKey(identity), identity);
  }
  return [...dependencies.values()].sort(
    (left, right) =>
      left.publisher.localeCompare(right.publisher) || left.key.localeCompare(right.key),
  );
}

export function organizationSettingsHref(
  organizationId: string,
  contributionId: string,
): string {
  return `/organizations/${encodeURIComponent(organizationId)}/settings/${encodeURIComponent(contributionId)}`;
}

function dialogLine(children: readonly unknown[], style: string): string | null {
  for (const child of children) {
    if (!child || typeof child !== "object") continue;
    const node = child as { type?: unknown; style?: unknown; text?: unknown };
    if (
      node.type === "text" &&
      node.style === style &&
      typeof node.text === "string" &&
      node.text.trim()
    ) {
      return node.text;
    }
  }
  return null;
}

function retrieveOperation(children: readonly unknown[]): string | null {
  for (const child of children) {
    if (!child || typeof child !== "object") continue;
    const button = child as { type?: unknown; command?: unknown };
    if (button.type !== "button" || typeof button.command !== "string") continue;
    const operation = ORGANIZATION_SETTINGS_RETRIEVE.exec(button.command)?.[1];
    if (operation) return operation;
  }
  return null;
}

function isDialogCopy(child: unknown): boolean {
  if (!child || typeof child !== "object") return false;
  const node = child as { type?: unknown; style?: unknown };
  return node.type === "text" && (node.style === "label" || node.style === "muted");
}

function v2View(value: unknown): { id: string; title: string; description?: string } | null {
  if (!value || typeof value !== "object") return null;
  const view = value as { id?: unknown; title?: unknown; description?: unknown };
  if (typeof view.id !== "string" || !V2_ID.test(view.id)) return null;
  if (typeof view.title !== "string" || !view.title.trim() || view.title.length > 150) return null;
  if (view.description !== undefined && (typeof view.description !== "string" || view.description.length > 500)) {
    return null;
  }
  return {
    id: view.id,
    title: view.title,
    ...(typeof view.description === "string" ? { description: view.description } : {}),
  };
}

function v2ViewContribution(value: unknown): { id: string; viewId: string } | null {
  if (!value || typeof value !== "object") return null;
  const contribution = value as {
    id?: unknown;
    app?: unknown;
    slot?: unknown;
    viewId?: unknown;
  };
  if (contribution.app !== "organizations" || contribution.slot !== V2_EXTENSION_VIEW_SLOT) {
    return null;
  }
  if (typeof contribution.id !== "string" || !V2_ID.test(contribution.id)) return null;
  if (typeof contribution.viewId !== "string" || !V2_ID.test(contribution.viewId)) return null;
  return { id: contribution.id, viewId: contribution.viewId };
}

/** Tabs an enabled installation contributes to the organization view. */
export function organizationSettingsTabs(
  installations: readonly OrganizationSettingsInstallation[],
  organizationId: string,
): OrganizationSettingsTab[] {
  const tabs: OrganizationSettingsTab[] = [];
  const seen = new Set<string>();
  const enabled = new Set<string>();
  const enabledIds = new Map<string, string>();
  for (const installation of installations) {
    const identity = installationIdentity(installation);
    if (installation.state !== "enabled" || !identity) continue;
    const key = identityKey(identity);
    enabled.add(key);
    if (typeof installation.id === "string" && INSTALLATION_ID.test(installation.id)) {
      enabledIds.set(key, installation.id);
    }
  }
  for (const installation of installations) {
    if (installation.state !== "enabled") continue;
    for (const contribution of installation.ui ?? []) {
      if (contribution.slot !== ORGANIZATION_SETTINGS_SLOT) continue;
      if (
        typeof contribution.id !== "string" ||
        !CONTRIBUTION_ID.test(contribution.id) ||
        seen.has(contribution.id)
      ) {
        continue;
      }
      const view = contribution.view;
      if (!view || typeof view !== "object") continue;
      const card = view as {
        type?: unknown;
        title?: unknown;
        description?: unknown;
        children?: unknown;
      };
      if (card.type !== "card" || typeof card.title !== "string" || !card.title.trim()) {
        continue;
      }
      if (!Array.isArray(card.children)) continue;
      const retrieving = dialogLine(card.children, "label");
      const unavailable = dialogLine(card.children, "muted");
      if (!retrieving || !unavailable) continue;
      const settingsTargets = installedDependencies(installation, enabled);
      const extensionInstallationId = settingsTargets.length === 1
        ? enabledIds.get(identityKey(settingsTargets[0]!)) ?? null
        : null;
      seen.add(contribution.id);
      tabs.push({
        format: "v1",
        id: contribution.id,
        label: card.title,
        href: organizationSettingsHref(organizationId, contribution.id),
        retrieving,
        unavailable,
        retrieveOperation: retrieveOperation(card.children),
        extensionInstallationId,
        settingsTargets,
        view: {
          type: "card",
          title: card.title,
          ...(typeof card.description === "string" ? { description: card.description } : {}),
          children: card.children.filter((child) => !isDialogCopy(child)),
        },
      });
    }
  }
  for (const installation of installations) {
    if (
      installation.state !== "enabled" ||
      installation.api_version !== "2.0" ||
      !installation.v2 ||
      typeof installation.id !== "string" ||
      !INSTALLATION_ID.test(installation.id)
    ) {
      continue;
    }
    const views = new Map<string, ReturnType<typeof v2View>>();
    for (const value of installation.v2.views ?? []) {
      const view = v2View(value);
      if (view) views.set(view.id, view);
    }
    for (const value of installation.v2.ui ?? []) {
      const contribution = v2ViewContribution(value);
      if (!contribution || seen.has(contribution.id)) continue;
      const view = views.get(contribution.viewId);
      if (!view) continue;
      seen.add(contribution.id);
      tabs.push({
        format: "v2",
        id: contribution.id,
        label: view.title,
        href: organizationSettingsHref(organizationId, contribution.id),
        extensionInstallationId: installation.id,
        viewId: contribution.viewId,
      });
    }
  }
  return tabs;
}
